import { supabase } from './supabase';

export const gamificationService = {
  // Member Points
  async getMemberPoints(userId, gymId) {
    try {
      const { data, error } = await supabase
        ?.from('member_points')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('gym_id', gymId)
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getPointsTransactions(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('points_transactions')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async addPoints(userId, gymId, pointsAmount, transactionType, sourceType, sourceId, description) {
    try {
      // Get current points
      const { data: memberPoints } = await this.getMemberPoints(userId, gymId);
      const currentBalance = memberPoints?.total_points || 0;
      const newBalance = currentBalance + pointsAmount;

      // Create transaction
      const { data: transaction, error: transactionError } = await supabase
        ?.from('points_transactions')
        ?.insert({
          user_id: userId,
          gym_id: gymId,
          points_amount: pointsAmount,
          transaction_type: transactionType,
          source_type: sourceType,
          source_id: sourceId,
          description: description,
          balance_after: newBalance
        })
        ?.select()
        ?.single();

      if (transactionError) throw transactionError;

      // Update member points
      const { data: updatedPoints, error: updateError } = await supabase
        ?.from('member_points')
        ?.upsert({
          user_id: userId,
          gym_id: gymId,
          total_points: newBalance,
          lifetime_points: (memberPoints?.lifetime_points || 0) + (pointsAmount > 0 ? pointsAmount : 0)
        })
        ?.select()
        ?.single();

      if (updateError) throw updateError;

      return { data: { transaction, updatedPoints }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Achievements
  async getAchievements(category = null) {
    try {
      let query = supabase?.from('achievements')?.select('*')?.eq('is_active', true);

      if (category) {
        query = query?.eq('category', category);
      }

      const { data, error } = await query?.order('tier')?.order('points_value');

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getMemberAchievements(userId) {
    try {
      const { data, error } = await supabase
        ?.from('member_achievements')
        ?.select(`
          *,
          achievement:achievements(*)
        `)
        ?.eq('user_id', userId)
        ?.order('earned_at', { ascending: false });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async unlockAchievement(userId, achievementId, gymId) {
    try {
      const { data, error } = await supabase
        ?.from('member_achievements')
        ?.upsert({
          user_id: userId,
          achievement_id: achievementId,
          gym_id: gymId,
          is_completed: true,
          completed_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Rewards
  async getRewardsCatalog(gymId, tierRequirement = null) {
    try {
      let query = supabase
        ?.from('rewards_catalog')
        ?.select('*')
        ?.eq('gym_id', gymId)
        ?.eq('is_active', true);

      if (tierRequirement) {
        query = query?.lte('tier_requirement', tierRequirement);
      }

      const { data, error } = await query?.order('points_cost');

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getRewardRedemptions(userId) {
    try {
      const { data, error } = await supabase
        ?.from('reward_redemptions')
        ?.select(`
          *,
          reward:rewards_catalog(*)
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async redeemReward(userId, rewardId, gymId, pointsCost) {
    try {
      // Generate redemption code
      const redemptionCode = `RWD-${Date.now()}-${Math.random()?.toString(36)?.substring(7)?.toUpperCase()}`;

      const { data, error } = await supabase
        ?.from('reward_redemptions')
        ?.insert({
          user_id: userId,
          reward_id: rewardId,
          gym_id: gymId,
          points_spent: pointsCost,
          redemption_code: redemptionCode,
          expires_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Deduct points
      await this.addPoints(userId, gymId, -pointsCost, 'redeemed', 'reward', rewardId, `Redeemed reward: ${redemptionCode}`);

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Referrals
  async getReferralStats(userId) {
    try {
      const { data, error } = await supabase
        ?.from('referral_program')
        ?.select('*')
        ?.eq('referrer_id', userId);

      const totalReferrals = data?.length || 0;
      const activeReferrals = data?.filter(r => r?.referral_status === 'active' || r?.referral_status === 'completed')?.length || 0;
      const totalPointsEarned = data?.reduce((sum, r) => sum + (r?.referrer_points_earned || 0), 0) || 0;

      return { 
        data: { 
          totalReferrals, 
          activeReferrals, 
          totalPointsEarned,
          referrals: data 
        }, 
        error 
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createReferral(referrerId, referredId, gymId, referralCode) {
    try {
      const { data, error } = await supabase
        ?.from('referral_program')
        ?.insert({
          referrer_id: referrerId,
          referred_id: referredId,
          gym_id: gymId,
          referral_code: referralCode,
          referral_status: 'pending',
          signup_date: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Challenges
  async getChallenges(gymId, status = 'active') {
    try {
      const { data, error } = await supabase
        ?.from('challenges')
        ?.select(`
          *,
          creator:user_profiles!created_by(id, full_name)
        `)
        ?.eq('gym_id', gymId)
        ?.eq('challenge_status', status)
        ?.order('start_date', { ascending: false });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getChallengeById(challengeId) {
    try {
      const { data, error } = await supabase
        ?.from('challenges')
        ?.select(`
          *,
          creator:user_profiles!created_by(id, full_name),
          participants:challenge_participants(
            *,
            user:user_profiles(id, full_name)
          )
        `)
        ?.eq('id', challengeId)
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async joinChallenge(challengeId, userId, teamName = null) {
    try {
      const { data, error } = await supabase
        ?.from('challenge_participants')
        ?.insert({
          challenge_id: challengeId,
          user_id: userId,
          team_name: teamName,
          participation_status: 'joined'
        })
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateChallengeProgress(participantId, progressAmount, progressNote = null) {
    try {
      // Get current progress
      const { data: participant } = await supabase
        ?.from('challenge_participants')
        ?.select('current_progress')
        ?.eq('id', participantId)
        ?.single();

      const newProgress = (participant?.current_progress || 0) + progressAmount;

      // Update participant progress
      const { data: updatedParticipant, error: updateError } = await supabase
        ?.from('challenge_participants')
        ?.update({ current_progress: newProgress })
        ?.eq('id', participantId)
        ?.select()
        ?.single();

      if (updateError) throw updateError;

      // Log progress
      const { data: progressLog, error: logError } = await supabase
        ?.from('challenge_progress_logs')
        ?.insert({
          participant_id: participantId,
          progress_amount: progressAmount,
          progress_note: progressNote
        })
        ?.select()
        ?.single();

      return { data: { updatedParticipant, progressLog }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Leaderboards
  async getLeaderboard(gymId, leaderboardType, timePeriod, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('leaderboards')
        ?.select(`
          *,
          user:user_profiles(id, full_name)
        `)
        ?.eq('gym_id', gymId)
        ?.eq('leaderboard_type', leaderboardType)
        ?.eq('time_period', timePeriod)
        ?.order('rank_position')
        ?.limit(limit);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getUserRank(userId, gymId, leaderboardType, timePeriod) {
    try {
      const { data, error } = await supabase
        ?.from('leaderboards')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('gym_id', gymId)
        ?.eq('leaderboard_type', leaderboardType)
        ?.eq('time_period', timePeriod)
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Social Interactions
  async createSocialInteraction(userId, targetUserId, interactionType, contentType, contentId, message = null) {
    try {
      const { data, error } = await supabase
        ?.from('gamification_social_interactions')
        ?.insert({
          user_id: userId,
          target_user_id: targetUserId,
          interaction_type: interactionType,
          content_type: contentType,
          content_id: contentId,
          message: message
        })
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }
};